import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResumeModel } from './resume.model';
import { InvoiceModel } from '../invoice/invoice/invoice.model';
import { TicketModel } from '../invoice/tickets/ticket.model';
import { ResumeV2Model } from './resumeV2.model';
import { SalaryModel } from '../expense/salary/salary.model';
import { SelfEmployedModel } from '../expense/selfemployed/selfemployed.model';
import { BalanceModel } from './balance/balance.model';
import { ResumeTodayModel } from './resume-today.model';
import { SummaryResultModel } from './summary-result.model';
import { ApiResponse } from '../../models/base';

const LIST_ENDPOINT = '/resume';
const GET_RESUME_INCOMES = '/resumeIncomes';
const GET_RESUME_EXPENSES = '/resumeExpenses';

@Injectable({
  providedIn: 'root',
})
export class ResumeService {
  private dataSubject = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient) {}

  getBalance(balance: BalanceModel): Promise<Blob> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + LIST_ENDPOINT + '/balance/', balance, {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  }

  getData(showGlobal: boolean): Promise<ResumeModel> {
    return firstValueFrom(
      this.http.get<ApiResponse<ResumeModel>>(environment.apiUrl + LIST_ENDPOINT + `/${showGlobal}`)
    ).then((response) => response.data);
  }

  getExpensesPaided(showGlobal: boolean, clientUid: string): Promise<InvoiceModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<InvoiceModel[]>>(
        environment.apiUrl + '/resume/expenses/paided' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getExpensesPending(showGlobal: boolean, clientUid: string): Promise<InvoiceModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<InvoiceModel[]>>(
        environment.apiUrl + '/resume/expenses/pending' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getIncomesPaided(showGlobal: boolean, clientUid: string): Promise<InvoiceModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<InvoiceModel[]>>(
        environment.apiUrl + '/resume/incomes/paided' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getIncomesPending(showGlobal: boolean, clientUid: string): Promise<InvoiceModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<InvoiceModel[]>>(
        environment.apiUrl + '/resume/incomes/pending' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getIncomesTicketsPaided(showGlobal: boolean, clientUid: string): Promise<TicketModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<TicketModel[]>>(
        environment.apiUrl + '/resume/incomes/tickets/paided' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getIncomesTicketsPending(showGlobal: boolean, clientUid: string): Promise<TicketModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<TicketModel[]>>(
        environment.apiUrl + '/resume/incomes/tickets/pending' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getSalariesPaided(showGlobal: boolean, clientUid: string): Promise<SalaryModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<SalaryModel[]>>(
        environment.apiUrl + '/resume/salaries/paided' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getSalariesPending(showGlobal: boolean, clientUid: string): Promise<SalaryModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<SalaryModel[]>>(
        environment.apiUrl + '/resume/salaries/Pending' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getSelfEmployedPaided(showGlobal: boolean, clientUid: string): Promise<SelfEmployedModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<SelfEmployedModel[]>>(
        environment.apiUrl + '/resume/selfemployed/paided' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getSelfEmployedPending(showGlobal: boolean, clientUid: string): Promise<SelfEmployedModel[]> {
    return firstValueFrom(
      this.http.get<ApiResponse<SelfEmployedModel[]>>(
        environment.apiUrl + '/resume/selfemployed/Pending' + `/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getDataV2(showGlobal: boolean, clientUid: string): Promise<ResumeV2Model> {
    return firstValueFrom(
      this.http.get<ApiResponse<ResumeV2Model>>(environment.apiUrl + LIST_ENDPOINT + `V2/${showGlobal}` + `/${clientUid}`)
    ).then((response) => response.data);
  }

  getResumeIncomes(showGlobal: boolean, clientUid: string): Promise<ResumeV2Model> {
    return firstValueFrom(
      this.http.get<ApiResponse<ResumeV2Model>>(environment.apiUrl + GET_RESUME_INCOMES + `/${showGlobal}` + `/${clientUid}`)
    ).then((response) => response.data);
  }

  getResumeTodayIncomes(showGlobal: boolean, clientUid: string): Promise<ResumeTodayModel> {
    return firstValueFrom(
      this.http.get<ApiResponse<ResumeTodayModel>>(
        environment.apiUrl + GET_RESUME_INCOMES + `/today/${showGlobal}` + `/${clientUid}`
      )
    ).then((response) => response.data);
  }

  getResumeExpenses(showGlobal: boolean, clientUid: string): Promise<ResumeV2Model> {
    return firstValueFrom(
      this.http.get<ApiResponse<ResumeV2Model>>(environment.apiUrl + GET_RESUME_EXPENSES + `/${showGlobal}` + `/${clientUid}`)
    ).then((response) => response.data);
  }

  exportIncomesResumeTable(uids: string[], clientUid: string): Promise<Blob> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + '/resume/export/incomes/' + clientUid, uids, {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  }

  exportExpensesResumeTable(uids: string[], clientUid: string): Promise<Blob> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + '/resume/export/expenses/' + clientUid, uids, {
        responseType: 'blob',
      })
    ).then((response) => {
      return new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  }

  getResumeResults(filter?: any): Promise<any> {
    let params = new HttpParams();

    if (filter && filter.isGlobal) {
      params = params.append('isGlobal', filter.isGlobal === true ? 'true' : 'false');
    }

    return firstValueFrom(
      this.http.get<ApiResponse<any>>(`${environment.apiUrl}/resume/results`, { params: params })
    ).then((response) => response.data);
  }

  getResumeModel(filter?: any): Promise<any> {
    let params = new HttpParams();

    if (filter && filter.isGlobal) {
      params = params.append('isGlobal', filter.isGlobal === true ? 'true' : 'false');
    }

    return firstValueFrom(
      this.http.get<ApiResponse<any>>(`${environment.apiUrl}/resume/models/retention`, { params: params })
    ).then((response) => response.data);
  }

  getReportCashFlow(): Promise<any> {
    return firstValueFrom(
      this.http.get<ApiResponse<any>>(`${environment.apiUrl}/reports/cash-flow`)
    ).then((response) => response.data);
  }

  getReportWorkingCapital(): Promise<any> {
    return firstValueFrom(
      this.http.get<ApiResponse<any>>(`${environment.apiUrl}/reports/working-capital`)
    ).then((response) => response.data);
  }

  getReportGrossMargin(): Promise<any> {
    return firstValueFrom(
      this.http.get<ApiResponse<any>>(`${environment.apiUrl}/reports/gross-margin`)
    ).then((response) => response.data);
  }

  sendData(data: any): void {
    this.dataSubject.next(data);
  }

  getBusinessAnalysis(): Promise<any> {
    return firstValueFrom(this.http.get<any>(environment.apiUrl + '/resume/business-analysis'))
      .then(response => (response['data'] as any[])[0])
      .catch(() => null);
  }

  getDataSubject(): Observable<any> {
    return this.dataSubject.asObservable();
  }
}

